/*!
 * bl-atc-fix.js — recovers the product identity on add_to_cart CDP events.
 *
 * THE PROBLEM THIS SOLVES
 * The CDP tracker (/apps/blt/s.js, served by the bl-cdp worker from R2) takes the
 * product handle from the PAGE URL, not from the add itself: it splits the current
 * path on "/products/". On a PDP that works. Everywhere else — collection grids,
 * search, quick-add, the cart drawer's "complete the look" row — there is no
 * /products/ in the URL, so the handle field is simply omitted from the payload and
 * the CDP's `|| "(unknown)"` fallback fires.
 *
 * Measured 18 Sep 2026: 44,439 of 152,335 add_to_cart rows (29.2%) carry
 * product_handle = '(unknown)'. Of those, 40,802 carry NO item code, NO product
 * name and NO size either — permanently unidentifiable. Every per-product cart
 * figure under-counts by that much, unevenly across products.
 *
 * WHY THIS LIVES IN THE THEME RATHER THAN IN THE TRACKER
 * The tracker is an R2 object (BLT_ASSETS/bl-track-nitro.js) plus a suffix the
 * bl-cdp worker appends at serve time. Replacing it needs BLT_ASSET_UPLOAD_KEY, and
 * changing the suffix needs a bl-cdp deploy — which currently only the outgoing
 * developer can do. The theme we control. And no worker change is needed, because
 * the CDP's ingest ALREADY reads the field:
 *     journey-d1.js: str(evField(e, "handle", "product_handle", "product") || "(unknown)", 300)
 * It records '(unknown)' only because the browser never sends it. So we send it.
 *
 * NO DOUBLE COUNTING
 * This fires ONLY where the tracker's URL trick fails — pages whose path carries no
 * valid /products/<handle>. On a PDP we stay silent, because the existing event is
 * already correct there. The handle test below mirrors crm-core.js's productHandle()
 * exactly so the two can never disagree about what counts as a PDP.
 *
 * KNOWN COST, deliberate: on a non-PDP add the tracker still posts its own
 * handle-less event, so that add lands as TWO rows — ours (correct) and its
 * ('(unknown)'). Every read query in bl-cdp already filters
 * product_handle <> '(unknown)', so per-product reporting picks ours and ignores
 * its; session-level cart counts (COUNT(DISTINCT session_id)) are unaffected
 * because both rows carry the same session. Only the raw add_to_cart EVENT count
 * inflates. Once this is proven in the data, the clean follow-up is to replace the
 * R2 tracker asset so the handle-less twin stops being emitted at all.
 *
 * PRIVACY: opt-in strict, same as the tracker. Nothing is sent unless analytics
 * consent is an explicit true — read from window.blt.getConsent() when the tracker has
 * loaded, otherwise decoded from the bl_consent cookie it wrote. Unknown is treated as
 * refused. No PII is read or sent — handle, title, size, price, quantity and variant id
 * only, all of it already public catalogue data.
 */
(function () {
  'use strict';

  var API = '/apps/blt/e';
  var MAX_WAIT_MS = 15000;   // how long to wait for the tracker's consent API
  var SETTLE_MS = 260;       // let Shopify commit the line before reading /cart.js

  // ── Is this a PDP? ───────────────────────────────────────────────────────────
  // Byte-for-byte the same rule as crm-core.js productHandle(). If this returns a
  // handle, the tracker already gets it right and we must not emit anything.
  function pdpHandle(u) {
    if (!u) return null;
    var i = String(u).indexOf('/products/');
    if (i < 0) return null;
    var h = String(u).slice(i + 10).split('?')[0].split('#')[0].split('/')[0];
    return h && /^[a-z0-9][a-z0-9-]*$/i.test(h) ? h : null;
  }

  if (pdpHandle(location.pathname)) return;   // PDP — nothing to fix, stay out.

  // ── Consent ──────────────────────────────────────────────────────────────────
  // Opt-in strict. Returns true (granted), false (refused) or null (not known yet);
  // we only ever send on an explicit true.
  //
  // Two sources, in order of authority:
  //   1. window.blt.getConsent() — the tracker's own API, which also reflects its
  //      GPC/DNT pre-rejection logic.
  //   2. the bl_consent cookie — base64-encoded JSON ({"v":1,"analytics":true,…})
  //      written by that same tracker, so it carries the same decision.
  //
  // ⚠️ The cookie fallback is not belt-and-braces, it is load-bearing: Shopify does
  // NOT inject app script tags on a theme PREVIEW, so /apps/blt/s.js never runs there
  // and window.blt never appears. Without this fallback the fix could not be tested
  // on a draft theme at all — only in production, which is exactly backwards.
  function analyticsGranted() {
    try {
      var b = window.blt;
      if (b && typeof b.getConsent === 'function') {
        var c = b.getConsent();
        return !!(c && c.analytics === true);
      }
    } catch (e) { /* fall through to the cookie */ }
    try {
      var raw = cookie('bl_consent');
      if (!raw) return null;                       // never asked, or never answered
      var s = raw.replace(/-/g, '+').replace(/_/g, '/');
      while (s.length % 4) s += '=';               // tolerate unpadded base64
      var j = JSON.parse(atob(s));
      return !!(j && j.analytics === true);
    } catch (e) { return null; }
  }

  // ── Cookies (first-party, ours) ──────────────────────────────────────────────
  // aid/sid are sent so the event joins the right visitor and session. The worker
  // also falls back to the bl_aid cookie server-side, but sid has no such fallback.
  function cookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }

  // ── Cart snapshot ────────────────────────────────────────────────────────────
  // We identify what was added by DIFFING /cart.js against the last snapshot,
  // rather than parsing the add request. The request body arrives in at least four
  // shapes across this theme (JSON {id,quantity}, FormData, a plain form POST, and
  // the drawer's own calls) and a variant id alone would still not give us the
  // handle, title or size. /cart.js gives all of it, authoritatively, in one call.
  var snap = Object.create(null);
  var haveSnap = false;

  function keyOf(line) {
    return String(line.variant_id || line.id || line.key || '');
  }

  function readCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function snapshot(cart) {
    var next = Object.create(null);
    if (cart && cart.items) {
      for (var i = 0; i < cart.items.length; i++) {
        var l = cart.items[i];
        next[keyOf(l)] = Number(l.quantity) || 0;
      }
    }
    snap = next;
    haveSnap = true;
  }

  // Which lines went UP since the snapshot. An add of a line already in the cart
  // shows as a quantity increase, not a new key — both are real adds.
  function addedLines(cart) {
    var out = [];
    if (!cart || !cart.items) return out;
    for (var i = 0; i < cart.items.length; i++) {
      var l = cart.items[i];
      var k = keyOf(l);
      var was = haveSnap && snap[k] != null ? snap[k] : 0;
      var now = Number(l.quantity) || 0;
      if (now > was) out.push({ line: l, delta: now - was });
    }
    return out;
  }

  // ── The event ────────────────────────────────────────────────────────────────
  // Field names are the ones journey-d1.js looks for, in its own order of
  // preference. `price` is Shopify's minor unit (paise), which is what the majority
  // of existing rows store and what the dashboard's
  // `CASE WHEN price>=10000 THEN price/100.0` normaliser expects.
  // `ts` is MILLISECONDS — the worker does tsSec(ms) => floor(ms/1000).
  // SIZE needs pulling apart. `variant_title` on this catalogue reads "COLOUR / SIZE"
  // ("LIGHTGREEN / S", "NAVY / 32") because every product carries option 1 = COLOR and
  // option 2 = SIZE. Stored raw, the colour lands in the size column and every colour×size
  // pair becomes its own value — hundreds of junk sizes instead of about seven, and nothing
  // groups. Verified against the catalogue: 12/12 sampled products use that exact order.
  //   Route 1 (exact)    — the cart line's own option list, taking the one NAMED size.
  //   Route 2 (fallback) — the text after the last "/".
  function sizeOf(line) {
    try {
      var o = line.options_with_values;
      if (o && o.length) {
        for (var i = 0; i < o.length; i++) {
          if (o[i] && /size/i.test(String(o[i].name || ''))) {
            var v = String(o[i].value == null ? '' : o[i].value).trim();
            if (v && v !== 'Default Title') return v;
          }
        }
      }
    } catch (e) { /* fall through to the title */ }
    var t = line.variant_title;
    if (!t || t === 'Default Title') return null;         // never invent a size
    var parts = String(t).split('/');
    return parts[parts.length - 1].trim() || null;
  }

  function toEvent(line, qty) {
    return {
      t: 'ecom',
      sub: 'add_to_cart',
      ts: Date.now(),
      url: location.href,
      handle: line.handle || null,
      name: line.product_title || line.title || null,
      sku: line.sku || null,
      size: sizeOf(line),
      price: line.price != null ? line.price : null,
      quantity: qty || 1,
      variant_id: line.variant_id != null ? String(line.variant_id) : null
    };
  }

  function send(events) {
    if (!events.length) return;
    var body = JSON.stringify({
      aid: cookie('bl_aid') || '',
      sid: cookie('bl_sid') || '',
      ev: events
    });
    try {
      // keepalive so the send survives the navigation a product-card click causes.
      fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        credentials: 'same-origin',
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* never let tracking break a cart action */ }
  }

  // ── Trigger ──────────────────────────────────────────────────────────────────
  var busy = false;

  function onAdd() {
    if (busy) return;              // one read per burst; the diff catches every line
    busy = true;
    setTimeout(function () {
      readCart().then(function (cart) {
        busy = false;
        if (!cart) return;
        if (analyticsGranted() !== true) { snapshot(cart); return; }
        var added = addedLines(cart);
        snapshot(cart);            // re-baseline whatever the outcome
        var evs = [];
        for (var i = 0; i < added.length; i++) {
          if (added[i].line.handle) evs.push(toEvent(added[i].line, added[i].delta));
        }
        send(evs);
      });
    }, SETTLE_MS);
  }

  function isAddUrl(u) {
    return /\/cart\/add(\.js)?(\?|$)/i.test(String(u || ''));
  }

  // fetch + XHR wrappers, and the theme's own events. We hook the same surfaces the
  // tracker does, so we cannot miss an add it catches.
  function install() {
    try {
      if (window.fetch && !window.__blAtcFixFetch) {
        window.__blAtcFixFetch = 1;
        var _f = window.fetch;
        window.fetch = function () {
          var u = arguments[0];
          u = (u && u.url) ? u.url : String(u || '');
          var p = _f.apply(this, arguments);
          try { if (isAddUrl(u)) p.then(function (r) { if (!r || r.ok) onAdd(); }).catch(function () {}); } catch (e) {}
          return p;
        };
      }
    } catch (e) {}

    try {
      if (window.XMLHttpRequest && !window.__blAtcFixXhr) {
        window.__blAtcFixXhr = 1;
        var _o = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function (m, u) {
          try { if (isAddUrl(u)) this.addEventListener('load', function () { onAdd(); }); } catch (e) {}
          return _o.apply(this, arguments);
        };
      }
    } catch (e) {}

    ['cart:added', 'cart:item-added', 'product:added-to-cart', 'ajaxProduct:added']
      .forEach(function (ev) {
        try { document.addEventListener(ev, function () { onAdd(); }); } catch (e) {}
      });
  }

  // ── Boot ─────────────────────────────────────────────────────────────────────
  // Baseline the cart first so the very first add on the page can be diffed, and
  // install the hooks immediately (an add can happen before consent resolves — the
  // diff still runs, it just won't send until analytics is granted).
  readCart().then(snapshot);
  install();

  // Nothing below is required for correctness; it only stops us holding the page's
  // fetch wrapper open forever on a visitor who never consents.
  var waited = 0;
  var poll = setInterval(function () {
    waited += 500;
    if (analyticsGranted() !== null || waited >= MAX_WAIT_MS) clearInterval(poll);
  }, 500);
})();
